import { CompetitionFilterOption, RankingFilterOption } from '../models/ranking-filter-models';
import { RankingCompetition } from '../models/ranking-models';

// The filter options come from the backend (title + competition); the icons are a pure
// frontend concern, keyed by the competition enum value (null = overall "Saison").
const SAISON_ICON = 'assets/icons/competitions/saison.svg';
const COMPETITION_ICONS: Record<RankingCompetition, string> = {
  BUNDESLIGA: 'assets/icons/competitions/bundesliga.jpg',
  DFB_POKAL: 'assets/icons/competitions/dfb-pokal.png',
  UEFA_CHAMPIONS_LEAGUE: 'assets/icons/competitions/champions-league.png',
  // TODO: no dedicated Euro-League asset yet — neutral fallback until provided.
  UEFA_EURO_LEAGUE: SAISON_ICON,
};

/**
 * Maps server-provided ranking filter options (global or group ranking) to the entries
 * of the shared competition filter modal. Several server options may share the same
 * competition value (e.g. "Saison", "Hinrunde" and the month ranking are all
 * `competition: null`) — callers must therefore identify the chosen option by its index.
 */
export function toCompetitionFilterOptions(options: RankingFilterOption[]): CompetitionFilterOption[] {
  return options.map((option) => ({
    value: option.competition,
    label: option.title,
    icon: option.competition ? COMPETITION_ICONS[option.competition] : SAISON_ICON,
    subtitle: option.subtitle,
  }));
}

/**
 * Finds the index of `selected` within `options`, identified by `orderIndex` — never by
 * `competition`, since several options can share `competition: null` (e.g. "Saison",
 * "Hinrunde" and the month ranking). Returns `null` when `selected` is `null` or not
 * found in `options`.
 *
 * Note: this normalizes the "not found" case to `null` rather than `Array.findIndex`'s
 * `-1`. Callers that feed the result into `CompetitionFilterModal`'s `selectedIndex`
 * input are unaffected either way — that component treats `-1` and `null` identically
 * (it only renders a match for a real option index, `0..options.length - 1`).
 */
export function findRankingFilterIndex(
  options: RankingFilterOption[],
  selected: RankingFilterOption | null,
): number | null {
  if (!selected) return null;
  const index = options.findIndex((option) => option.orderIndex === selected.orderIndex);
  return index === -1 ? null : index;
}
